<template>
    <transition name="consent-fade">
        <div v-if="isVisible" class="consent-banner" role="dialog" aria-live="polite" aria-label="Cookies">
            <div class="consent-card">
                <div class="consent-title">Cookies</div>
                <p class="consent-text">
                    Необходимые cookies - для работы сервиса. Аналитические - только с вашего согласия.
                </p>
                <p class="consent-subtext">Выбор можно изменить в настройках.</p>
                <div class="consent-links">
                    <RouterLink to="/legal/privacy-policy" class="consent-link">
                        Политика cookies и конфиденциальности
                    </RouterLink>
                </div>
                <div class="consent-meta">Только необходимые cookies включены всегда</div>
                <div class="consent-actions">
                    <v-btn variant="flat" class="consent-btn consent-btn-secondary" @click="declineAnalytics">
                        Не принимаю
                    </v-btn>
                    <v-btn color="primary" variant="flat" class="consent-btn consent-btn-primary" @click="acceptAnalytics">
                        Принимаю
                    </v-btn>
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

import { analytics } from '@/lib/analytics';
import { readConsent, setConsent, withdrawConsent } from '@/shared/lib/consent';

const isVisible = ref(false);

const syncVisibility = () => {
    isVisible.value = readConsent() === 'unknown';
};

const acceptAnalytics = () => {
    setConsent('granted', { source: 'banner' });
    isVisible.value = false;
    analytics.trackPageView(window.location.href);
};

const declineAnalytics = () => {
    setConsent('denied', { source: 'banner' });
    withdrawConsent();
    isVisible.value = false;
};

const onStorageChange = (event: StorageEvent) => {
    if (!event.key || event.key.includes('consent')) {
        syncVisibility();
    }
};

const onConsentChange = () => {
    syncVisibility();
};

onMounted(() => {
    syncVisibility();
    window.addEventListener('storage', onStorageChange);
    window.addEventListener('analytics-consent-changed', onConsentChange);
});

onBeforeUnmount(() => {
    window.removeEventListener('storage', onStorageChange);
    window.removeEventListener('analytics-consent-changed', onConsentChange);
});
</script>

<style scoped>
.consent-banner {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2400;
    display: flex;
    justify-content: center;
    padding: 16px;
    pointer-events: none;
}

.consent-card {
    width: min(920px, 100%);
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
    padding: 16px;
    pointer-events: auto;
}

.consent-title {
    font-size: 16px;
    line-height: 1.3;
    font-weight: 700;
    color: #111827;
}

.consent-text {
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 1.4;
    color: #374151;
}

.consent-subtext {
    margin: 4px 0 0;
    font-size: 13px;
    line-height: 1.35;
    color: #6b7280;
}

.consent-links {
    margin-top: 8px;
}

.consent-link {
    color: #7b65f7;
    font-size: 13px;
    text-decoration: none;
    font-weight: 600;
}

.consent-link:hover {
    text-decoration: underline;
}

.consent-meta {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.35;
    color: #6b7280;
}

.consent-actions {
    margin-top: 14px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.consent-btn {
    min-height: 44px;
    border-radius: 12px !important;
    padding: 10px 18px;
    text-transform: none;
    font-size: 0.95rem;
    font-weight: 800;
}

.consent-btn-secondary {
    border: none !important;
    box-shadow: none !important;
    color: #374151 !important;
    background: #f3f4f6 !important;
}

.consent-btn-primary {
    background: linear-gradient(120deg, rgba(123, 101, 247, 0.96) 0%, rgba(123, 101, 247, 0.82) 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 10px 20px rgba(123, 101, 247, 0.24);
}

.consent-btn-primary:hover {
    box-shadow: 0 12px 24px rgba(123, 101, 247, 0.28);
    transform: translateY(-1px);
}

.consent-btn :deep(.v-btn__overlay),
.consent-btn :deep(.v-btn__underlay),
.consent-btn :deep(.v-btn__content) {
    border-radius: 12px !important;
}

.consent-fade-enter-active,
.consent-fade-leave-active {
    transition: opacity 0.2s ease;
}

.consent-fade-enter-from,
.consent-fade-leave-to {
    opacity: 0;
}

@media (max-width: 768px) {
    .consent-banner {
        padding: 10px;
    }

    .consent-card {
        padding: 12px;
    }

    .consent-actions {
        justify-content: stretch;
        gap: 8px;
    }

    .consent-actions :deep(.v-btn) {
        width: 100%;
    }
}
</style>
